import React, {memo, FC, useEffect, useState} from "react";
import {FormikProps, FieldArray} from "formik";
import {Alert, Button, Modal, Form, Tooltip} from "antd";
import {ApolloError} from "@apollo/client";
import moment from "moment";
import i18n from "../../../services/i18n";
import {
    useGetInstallmentPlanQuery,
    NewInstallmentPlanInput,
    PaymentType,
    PaymentInterval,
    CreateInstallmentRateByCourseCreateInput,
    Maybe,
    CollitionCheckInput,
    useGetMultipleCourseLessonsCollisionCheckQuery,
    GetMultipleCourseLessonsCollisionCheckQuery,
    useGetHolidaysByCompanyAndDateRangeQuery,
    GetHolidaysByCompanyAndDateRangeQuery,
    EditCourseLessonInput,
    GetBookingsByCourseIdWithCourseLessonBookingsQuery,
    useGetLessonsThatAreNotDeletableQuery,
    GetLessonsThatAreNotDeletableQuery,
} from "../../../generated/graphql";
import CourseCreateDateList from "../CourseCreateDateList";
import LayoutStep from "../../../views/LayoutStep/LayoutStep";
import SectionHeader from "../../../views/SectionHeader/SectionHeader";
import LayoutTableWrapper from "../../../views/LayoutTableWrapper/LayoutTableWrapper";
import {handleDefaultDateChange, handleDefaultCourseCycleChange, handleDefaultTimeChange, generateLessons, addLesson, lessonsAreEqual} from "../courseHelpers";
import {CourseNumberOfLessonsInput} from "../CourseViews/CourseStepViews";
import CourseDateInput from "../../../views/CourseDateInput/CourseDateInput";
import {createInputHelpers} from "../../../helpers/createInputHelpers";
import CourseCycleInput from "../../../views/CourseCycleInput/CourseCycleInput";
import CourseTimeInput from "../../../views/CourseTimeInput/CourseTimeInput";
import TimeInputError from "../CourseEdit/TimeInputError";
import Description from "../CourseViews/Description";
import {getlessonId} from "../helper/getLessonId";
import css from "./LessonBookings.less";
import ModifyBookingsModal from "./ModifyBookingsModal";
import {PossibleFormikPropsType} from "../CourseCreate/CourseCreateForm";

// export type PossibleFormikPropsType = NewCourseInput | EditCourseInput;

type StepCourseLessonsLessonBookingsProps = {
    formikProps: FormikProps<PossibleFormikPropsType>;
    error: ApolloError | Error | undefined;
    courseNumber?: string;
    nextStepButton: React.ReactElement;
    subtitle?: Maybe<string> | React.ReactNode;
    style?: React.CSSProperties;
    bookingsOfCourse?: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"];
    hasBilledLessons?: boolean;
    billedLessonIds?: string[];
};

const StepCourseLessonsLessonBookings: FC<StepCourseLessonsLessonBookingsProps> = ({
    formikProps,
    error,
    courseNumber,
    nextStepButton,
    subtitle,
    style,
    bookingsOfCourse,
    hasBilledLessons = false,
    billedLessonIds = [],
}) => {
    const [hasBeenEdited, setHasBeenEdited] = useState<boolean>(false);
    const {values} = formikProps;
    const {help, validateStatus} = createInputHelpers<PossibleFormikPropsType>(formikProps);
    const [timeError, setTimeError] = useState<string | undefined>(undefined);
    const [holidays, setHolidays] = useState<GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"]>([]);
    const courseId = values.id;
    const currentLessons: Array<EditCourseLessonInput> | undefined = values.lessons;
    const initialLessons: Array<EditCourseLessonInput> | undefined = formikProps.initialValues.lessons;
    const lessonsAreChanged = !lessonsAreEqual(currentLessons, initialLessons);
    const hideEditor = bookingsOfCourse && bookingsOfCourse.length > 0;
    const disableEditor = !hideEditor && hasBilledLessons;

    useEffect(() => {
        if (formikProps.values === formikProps.initialValues) {
            setHasBeenEdited(false);
        } else {
            setHasBeenEdited(true);
        }
    }, [formikProps]);

    // HANDLE COLLISIONS --------------------------------------------------------------------------
    const lessons = values.lessons;
    const getInput = (): Array<CollitionCheckInput> => {
        return lessons.map((lesson, index) => {
            return {
                venueId: lesson.venueId,
                courseLessonId: getlessonId(lesson),
                courseLessonIndex: index,
                startDateTime: lesson.startDateTime,
                endDateTime: lesson.endDateTime,
            };
        }) as Array<CollitionCheckInput>;
    };
    const collitionCheckInput = getInput();
    const {
        data: collisionData,
        // loading: collisionLoading,
        // error: collisionError,
    } = useGetMultipleCourseLessonsCollisionCheckQuery({
        skip: collitionCheckInput.length === 0,
        variables: {
            collitionCheckInput,
        },
        fetchPolicy: "network-only",
    });

    const collitions: GetMultipleCourseLessonsCollisionCheckQuery["multipleCourseLessonsCollisionCheck"] = collisionData?.multipleCourseLessonsCollisionCheck ?? [];

    // HANDLE LESSONS THAT ARE NOT DELETABLE --------------------------------------------------------------------------
    const courseLessonIds = lessons.map((lesson) => getlessonId(lesson)).filter((id) => id !== undefined) as Array<string>;
    const {data: lessonsThatAreNotDeletableData} = useGetLessonsThatAreNotDeletableQuery({
        skip: courseLessonIds.length === 0,
        variables: {courseLessonIds},
        fetchPolicy: "network-only",
    });

    const lessonsThatAreNotDeletable: GetLessonsThatAreNotDeletableQuery["getLessonsThatAreNotDeletable"] = lessonsThatAreNotDeletableData?.getLessonsThatAreNotDeletable ?? [];

    // HANDLE HOLIDAYS ---------------------------------------------------------------------------------

    const start = moment(values.lessons[0].startDateTime).startOf("day").format();
    const end = moment(values.lessons[values.lessons.length - 1].endDateTime)
        .endOf("day")
        .format();

    const {data: holidayData, loading: loadingHoldiays} = useGetHolidaysByCompanyAndDateRangeQuery({
        skip: values.lessons.length === 0,
        variables: {
            start,
            end,
        },
    });

    useEffect(() => {
        if (holidayData && !loadingHoldiays) {
            setHolidays(holidayData.holidaysByCompanyAndDateRange);
        }
    }, [holidayData, loadingHoldiays]);

    // HANDLE INSTALLMENT PLAN --------------------------------------------------------------------------

    const [currentInstallmentRates, setCurrentInstallmentRates] = useState<Array<CreateInstallmentRateByCourseCreateInput> | undefined>(formikProps.values.installmentRates ?? undefined);

    const getNewInstallmentPlanInput = (formikProps: FormikProps<PossibleFormikPropsType>): NewInstallmentPlanInput => {
        const {lessons, monthlySchedule, paymentInterval, vat, fee} = values;
        const sortedInstallmentRates = values.installmentRates?.slice().sort((a, b) => a.installmentDue - b.installmentDue);
        const dateOfFirstInstallment = sortedInstallmentRates?.[0]?.installmentDue;
        const courseFee = fee ?? 0;
        const courseVat = vat ?? 0;

        const grossPrice = paymentInterval === "PerCourse" ? courseFee : courseFee * lessons.length;

        const newInstallmentPlanInput: NewInstallmentPlanInput = {
            coursePrice: grossPrice,
            vat: courseVat,
            paymentInterval: paymentInterval ?? PaymentInterval.PerCourse,
            // Zahlungsart des Kurses, sonst erhält jeder Vorkasse-Kurs beim Bearbeiten einen Monatsplan
            paymentType: values.paymentType ?? PaymentType.Monthly,
            monthlySchedule,
            courseLessonStartDates: lessons.map((lesson) => lesson.startDateTime),
            dateOfFirstInstallment,
        };

        return newInstallmentPlanInput;
    };

    const {data: installmentPlanData} = useGetInstallmentPlanQuery({
        variables: {
            newInstallmentPlanInput: getNewInstallmentPlanInput(formikProps),
        },
        fetchPolicy: "network-only",
    });

    const installmentRates = installmentPlanData?.getInstallmentPlanPreview?.schedule;

    useEffect(() => {
        if (installmentRates && installmentRates !== currentInstallmentRates && values.allowMonthlyPayment) {
            console.log("SETTING INSTALLMENT RATES", installmentRates);
            setCurrentInstallmentRates(installmentRates);
            formikProps.setFieldValue("installmentRates", installmentRates);
        }
    }, [currentInstallmentRates, formikProps, installmentRates, values.allowMonthlyPayment]);

    const lessonCountInputError = values.defaultLessonCount === 0 || values.defaultLessonCount === null;
    const allowLessonBooking = values.allowLessonBooking;

    // HANDLE LESSONS ----------------------------------------------------------------------------------
    const editorStyle: React.CSSProperties = {
        gridColumn: "1 / span 4",
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gridGap: "1rem",
        backgroundColor: "#f0f0f0",
        padding: "1rem",
        ...(disableEditor
            ? {
                  opacity: 0.6,
                  pointerEvents: "none" as const,
                  cursor: "not-allowed" as const,
              }
            : {}),
    };

    return (
        <LayoutStep columns={4} title={i18n.containers.courses.CourseCreate.courseStepLessons.title()} subtitle={subtitle} error={error} button={nextStepButton} style={style} lessonBooking>
            {disableEditor && (
                <Alert type="info" showIcon message={i18n.containers.courses.CourseEdit.billedLessons.settingsDisabled.basicSettings()} style={{gridColumn: "1 / span 4", marginBottom: "1rem"}} />
            )}
            {!hideEditor && (
                <div style={editorStyle}>
                    <SectionHeader level={2} text={i18n.containers.courses.CourseCreate.courseStepLessons.sections.settings()} inlineContent={<Description />} />
                    <CourseDateInput
                        label={i18n.containers.courses.CourseCreate.firstCourseLessonDate.label()}
                        value={values.startDateTime ?? undefined}
                        onChange={handleDefaultDateChange(formikProps)}
                        validateStatus={validateStatus("startDateTime")}
                        help={help("startDateTime")}
                        testId="CourseDateInput"
                    />

                    <CourseCycleInput
                        label={i18n.containers.courses.CourseCreate.courseCycle.label()}
                        placeholder={i18n.containers.courses.CourseCreate.courseCycle.placeholder()}
                        value={values.cycle ?? undefined}
                        onChange={handleDefaultCourseCycleChange(formikProps)}
                        validateStatus={validateStatus("cycle")}
                        help={help("cycle")}
                        testId="CourseCycleInput"
                    />
                    <CourseTimeInput
                        label={i18n.containers.courses.CourseCreate.courseStartDateTime.label()}
                        placeholder={i18n.containers.courses.CourseCreate.courseStartDateTime.placeholder()}
                        value={values.startDateTime ?? undefined}
                        onChange={handleDefaultTimeChange(formikProps, "startDateTime")}
                        validateStatus={validateStatus("startDateTime")}
                        help={help("startDateTime")}
                        testId="CourseStartDateTimeInput"
                    />

                    <CourseTimeInput
                        label={i18n.containers.courses.CourseCreate.courseEndDateTime.label()}
                        placeholder={i18n.containers.courses.CourseCreate.courseEndDateTime.placeholder()}
                        value={values.endDateTime ?? undefined}
                        onChange={handleDefaultTimeChange(formikProps, "endDateTime")}
                        validateStatus={timeError as any}
                        help={<TimeInputError startDateTime={values.startDateTime} endDateTime={values.endDateTime} setTimeError={setTimeError} />}
                        testId="CourseEndDateTimeInput"
                    />
                    <CourseNumberOfLessonsInput label={i18n.containers.courses.CourseCreate.courseNumberOfLessons.label()} formikProps={formikProps} testId="CourseNumberOfLessonsInput" />
                    <div
                        style={{
                            gridColumn: "1 / span 4",
                        }}
                    >
                        <Tooltip
                            title={
                                timeError
                                    ? i18n.containers.courses.CourseCreate.courseEndDateTime.endTimeBeforeStart()
                                    : lessonCountInputError
                                    ? i18n.containers.courses.CourseCreate.defaultLessonCount.errorMsg()
                                    : undefined
                            }
                        >
                            <Button
                                block
                                // type="primary"
                                type={hasBeenEdited ? "primary" : "default"}
                                disabled={timeError !== undefined || lessonCountInputError}
                                onClick={() => {
                                    if (courseNumber) {
                                        Modal.confirm({
                                            icon: null,
                                            style: {whiteSpace: "pre-line"},
                                            title: i18n.containers.courses.CourseCreate.modals.recalculateLessons.title(),
                                            content: i18n.containers.courses.CourseCreate.modals.recalculateLessons.description(),
                                            okText: i18n.containers.courses.CourseCreate.modals.recalculateLessons.ok(),
                                            cancelText: i18n.containers.courses.CourseCreate.modals.recalculateLessons.cancel(),
                                            onOk: () => generateLessons(formikProps),
                                        });
                                    } else {
                                        generateLessons(formikProps);
                                    }
                                }}
                            >
                                {i18n.containers.courses.CourseCreate.courseStepLessons.recalculateButton()}
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            )}
            <SectionHeader level={2} text={i18n.containers.courses.CourseCreate.courseStepLessons.sections.lessons()} style={{marginTop: hideEditor ? "0" : "1rem"}} />
            {allowLessonBooking && (
                <ModifyBookingsModal courseId={courseId} courseNumber={courseNumber} formikProps={formikProps} bookingsOfCourse={bookingsOfCourse} lessonsAreChanged={lessonsAreChanged} />
            )}
            <LayoutTableWrapper>
                <FieldArray
                    name="lessons"
                    render={({remove}) => {
                        return (
                            <>
                                <CourseCreateDateList
                                    data-testid="CourseLessonTable"
                                    formikProps={formikProps}
                                    handleRemove={remove}
                                    collitions={collitions}
                                    holidays={holidays}
                                    lessonsThatAreNotDeletable={lessonsThatAreNotDeletable}
                                    billedLessonIds={billedLessonIds}
                                />
                                <Form.Item style={{gridColumn: "1 / -1"}}>
                                    <Button
                                        data-testid="CourseLessonAddButton"
                                        size="small"
                                        style={{width: "100%"}}
                                        type="dashed"
                                        onClick={() => addLesson(formikProps)}
                                        disabled={formikProps.values.lessons.length >= 53}
                                    >
                                        {i18n.containers.courses.CourseCreate.addLesson()}
                                    </Button>
                                </Form.Item>
                            </>
                        );
                    }}
                />
            </LayoutTableWrapper>
        </LayoutStep>
    );
};

export default memo(StepCourseLessonsLessonBookings);
